/**
 * SMEOverview — /sme/overview
 * SME Workspace Overview — Organization profile, monthly status, quick navigation
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, TrendingDown, Target, Shield, FileText, BarChart2,
         ChevronRight, Building2, AlertTriangle, CheckCircle2 } from "lucide-react";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import OnboardingOverlay from "@/components/OnboardingOverlay";
import NextActionPanel from "@/components/NextActionPanel";
import WorkspaceHeader from "@/components/WorkspaceHeader";
import { useDemoMode } from "@/hooks/useDemoMode";
import { DemoToggle, DemoBanner, DemoDataDisplay } from "@/components/DemoDataOverlay";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
export default function SMEOverview() {
  const navigate   = useNavigate();
  const { currentOrg } = useAuth();
  const { demoMode, toggleDemo, exitDemo } = useDemoMode("sme");
  const [journey,  setJourney]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [periods,  setPeriods]  = useState(null);

  useEffect(() => {
    Promise.all([
      client.get("/sme/my-journey").catch(() => null),
      client.get("/sme/periodic/periods").catch(() => null),
    ]).then(([jRes, pRes]) => {
      setJourney(jRes?.data || null);
      setPeriods(pRes?.data || null);
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const org      = currentOrg || {};
  const footprint= journey?.footprint;
  const periods_count = periods?.total || 0;
  const locked_count  = (periods?.periods || []).filter(p => p.is_locked).length;

  const steps = [
    { label: "Monthly Input Data",              path: "/sme/monthly/inputs",   icon: BarChart2, done: periods_count > 0 },
    { label: "Climate Snapshot",                path: "/sme/climate-snapshot", icon: TrendingDown, done: footprint?.total_tco2e > 0 },
    { label: "Climate & Circularity Credentials",path:"/sme/credentials",      icon: Shield, done: locked_count > 0 },
    { label: "SDG Contribution",                path: "/sme/sdg",              icon: Target, done: footprint?.total_tco2e > 0 },
    { label: "Portfolio Intelligence",           path: "/sme/monthly/trends",   icon: BarChart2, done: periods_count >= 3 },
    { label: "Evidence Vault",                   path: "/evidence",             icon: FileText, done: false },
    { label: "Reports",                          path: "/sme-reports",          icon: FileText, done: false },
    { label: "Financing Readiness",              path: "/sme/financing-readiness",icon: Target, done: false },
    { label: "Climate Training",                 path: "/academy",              icon: Target, done: false },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-6">
        {/* First-time onboarding overlay */}
        <OnboardingOverlay workspace="sme" />

        {/* Workspace header with flow indicator + demo toggle */}
        <WorkspaceHeader
          workspace="sme"
          entityName={demoMode ? "Gasavant Africa Ltd (Demo)" : (org.name || "My Organization")}
          subTitle={demoMode ? "Energy Services · Nigeria · Lagos State · Example Data" : [org.sector, org.country, org.employee_count ? `${org.employee_count} employees` : null].filter(Boolean).join(" · ")}
          stats={demoMode ? [
            { label: "Periods (Demo)", value: "6", color: "text-amber-400" },
            { label: "Locked (Demo)", value: "3", color: "text-emerald-400" },
            { label: "Tracking", value: "Active", color: "text-teal-400" },
            { label: "YTD (Demo)", value: "24.5 tCO2e", color: "text-amber-400" },
          ] : [
            { label: "Periods Calculated", value: periods_count.toString(), color: "text-teal-400" },
            { label: "Periods Locked",     value: locked_count.toString(),  color: "text-emerald-400" },
            { label: "Monthly Tracking",   value: periods_count > 0 ? "Active" : "Not started", color: periods_count > 0 ? "text-teal-400" : "text-slate-400" },
            { label: "Status",            value: footprint?.total_tco2e ? `${footprint.total_tco2e.toFixed(1)} tCO2e` : "No data", color: "text-amber-400" },
          ]}
          className="mb-5">
          <div className="mt-2">
            <DemoToggle workspace="sme" demoMode={demoMode} onToggle={toggleDemo} />
          </div>
        </WorkspaceHeader>

        {/* Demo banner */}
        {demoMode && <DemoBanner workspace="sme" onExit={exitDemo} />}

        {/* Demo data display — replaces real content when active */}
        {demoMode ? (
          <DemoDataDisplay workspace="sme" />
        ) : (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <MetricCard label="Periods Calculated" value={periods_count} color="text-teal-600" />
              <MetricCard label="Periods Locked"     value={locked_count}  color="text-emerald-600" />
              <MetricCard label="Total Footprint"    value={footprint?.total_tco2e ? `${footprint.total_tco2e.toFixed(1)} t` : "—"} color="text-amber-600" />
              <MetricCard label="Monthly Tracking"   value={periods_count > 0 ? "Active" : "Not started"} color={periods_count > 0 ? "text-teal-600" : "text-slate-400"} />
            </div>

            {/* Next action panel */}
            <NextActionPanel workspace="sme" className="mb-5" />

        {/* Workspace Navigation */}
        <SectionCard title="Workspace Modules" noPadding className="mb-6">
          <div className="divide-y divide-slate-50">
            {steps.map((step, i) => (
              <button key={step.label} onClick={() => navigate(step.path)}
                className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 text-left transition-colors">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                    step.done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                  }`}>{i + 2}</span>
                  <p className={`text-sm font-medium ${step.done ? "text-slate-900" : "text-slate-500"}`}>{step.label}</p>
                  {step.done && <CheckCircle2 size={12} className="text-emerald-500" />}
                </div>
                <ChevronRight size={14} className="text-slate-400" />
              </button>
            ))}
          </div>
        </SectionCard>

        {/* Quick action */}
        {periods_count === 0 && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 text-center">
            <p className="text-sm font-semibold text-teal-700">Start your first monthly calculation</p>
            <p className="text-xs text-teal-600 mt-1 mb-3">Enter monthly activity data to begin tracking your emissions.</p>
            <CTAButton variant="action" onClick={() => navigate("/sme/monthly/inputs")}>Enter Monthly Data →</CTAButton>
          </div>
        )}
          </>
        )}
    </div>
  );
}
